import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Modal,
  TextInput,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Dimensions,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../../utils/apiClient';

const { width, height } = Dimensions.get('window');

// ────────────────────────────────────────────────────────────────────────────
// DB rows come back snake_case; the rest of the screen reads camelCase. One
// normalizer keeps the rest of the file untouched — every property the UI
// reads is filled from whichever shape the API returns.
// Also normalises 'is_active'/'status' to a single 'status' enum the UI uses
// (active | draft | paused | closed) so the tab filter keeps working.
// ────────────────────────────────────────────────────────────────────────────
const normalizeJob = (j) => {
  if (!j) return null;
  const rawStatus = (j.status || '').toLowerCase();
  const isActive = j.is_active === true || j.is_active === 'true';
  const computedStatus = rawStatus
    || (isActive ? 'active' : (j.published === false ? 'draft' : 'active'));
  return {
    ...j,
    id: String(j.id),
    title: j.title || 'Untitled',
    department: j.department || '',
    location: j.location || '',
    employmentType: j.employment_type || j.job_type || j.employmentType || 'full-time',
    status: computedStatus,
    salaryMin: j.salary_min ?? j.salaryMin ?? '',
    salaryMax: j.salary_max ?? j.salaryMax ?? '',
    salaryCurrency: j.salary_currency || j.salaryCurrency || 'GBP',
    description: j.description || '',
    requirements: j.requirements || '',
    benefits: j.benefits || '',
    isUrgent: !!(j.is_urgent || j.isUrgent),
    isRemote: !!(j.is_remote || j.isRemote || (j.location || '').toLowerCase().includes('remote')),
    isFeatured: !!(j.is_featured || j.isFeatured),
    applicants: Number(j.applicant_count ?? j.applicants ?? 0),
    views: Number(j.views ?? j.view_count ?? 0),
    createdAt: j.created_at || j.createdAt || null,
    updatedAt: j.updated_at || j.updatedAt || null,
    company_id: j.company_id,
    skills: Array.isArray(j.required_skills) ? j.required_skills
          : Array.isArray(j.skills)          ? j.skills
          : typeof j.required_skills === 'string' ? j.required_skills.split(',').map(s => s.trim()).filter(Boolean)
          : [],
  };
};

export default function JobManagementScreen({ navigation, route }) {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [sortBy, setSortBy] = useState('created-desc');
  const [filters, setFilters] = useState({
    department: '',
    location: '',
    employmentType: '',
    status: '',
  });

  // Tab configuration
  const tabs = [
    { id: 'all', label: 'All Jobs', icon: 'briefcase' },
    { id: 'active', label: 'Active', icon: 'briefcase-check' },
    { id: 'draft', label: 'Draft', icon: 'file-document-outline' },
    { id: 'paused', label: 'Paused', icon: 'pause-circle' },
    { id: 'closed', label: 'Closed', icon: 'briefcase-remove' },
  ];

  // Filter options
  const departments = [
    'Engineering', 'Product', 'Design', 'Data Science', 'DevOps', 'QA',
    'Marketing', 'Sales', 'Business Development', 'Operations', 'HR', 'Finance',
    'Legal', 'Customer Success', 'Support', 'Research', 'Security'
  ];

  const locations = [
    'London', 'Manchester', 'Birmingham', 'Edinburgh', 'Glasgow', 'Bristol',
    'Leeds', 'Liverpool', 'Newcastle', 'Cardiff', 'Belfast', 'Remote',
    'Hybrid - London', 'Hybrid - Manchester', 'Multiple Locations'
  ];

  const employmentTypes = [
    { label: 'Full-time', value: 'full-time' },
    { label: 'Part-time', value: 'part-time' },
    { label: 'Contract', value: 'contract' },
    { label: 'Freelance', value: 'freelance' },
    { label: 'Internship', value: 'internship' },
    { label: 'Graduate', value: 'graduate' },
    { label: 'Apprenticeship', value: 'apprenticeship' }
  ];

  const sortOptions = [
    { label: 'Newest First', value: 'created-desc' },
    { label: 'Oldest First', value: 'created-asc' },
    { label: 'Title A-Z', value: 'title-asc' },
    { label: 'Title Z-A', value: 'title-desc' },
    { label: 'Most Applicants', value: 'applicants-desc' },
    { label: 'Least Applicants', value: 'applicants-asc' },
    { label: 'Highest Salary', value: 'salary-desc' },
    { label: 'Lowest Salary', value: 'salary-asc' },
  ];

  useEffect(() => {
    loadJobs();
    loadCompanyProfile();

    // Listen for navigation focus to reload jobs
    const unsubscribe = navigation.addListener('focus', () => {
      loadJobs();
      loadCompanyProfile();
    });

    return unsubscribe;
  }, [navigation]);

  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') ||
                    await AsyncStorage.getItem('token');
      const res = await fetch(
        `https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp && typeof cp === 'object') {
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (logo) setCompanyLogo(logo);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const cid = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
      if (!cid) {
        setJobs([]);
        Alert.alert('No company', 'Could not determine your company. Please sign in again.');
        return;
      }
      // Real API — scoped to this company. Pulling broad filters server-side
      // so the tab filter (all / active / draft / paused) works on real data.
      const res = await apiFetch(`/api/data/jobs?company_id=${cid}&limit=500&sort=created_at&order=desc`);
      const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      const normalized = rows.map(normalizeJob).filter(Boolean);
      setJobs(normalized);
      // Keep a local mirror for the offline case (Read-only — the truth is the DB)
      await AsyncStorage.setItem('jobs_cache', JSON.stringify(normalized)).catch(() => {});
    } catch (error) {
      console.warn('loadJobs:', error?.message);
      // Last-resort: try the offline cache if we have one
      const cached = await AsyncStorage.getItem('jobs_cache');
      if (cached) {
        try { setJobs(JSON.parse(cached)); } catch { setJobs([]); }
      } else {
        setJobs([]);
        Alert.alert('Could not load jobs', error?.message || 'Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadJobs();
    setRefreshing(false);
  };

  // Filter and sort jobs
  const getFilteredAndSortedJobs = () => {
    let filteredJobs = jobs.filter(job => {
      // Tab filter
      const matchesTab = selectedTab === 'all' || job.status === selectedTab;
      
      // Search filter
      const matchesSearch = !searchQuery || 
        String(job.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(job.department ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(job.location ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      
      // Advanced filters
      const matchesDepartment = !filters.department || job.department === filters.department;
      const matchesLocation = !filters.location || job.location === filters.location;
      const matchesEmploymentType = !filters.employmentType || job.employmentType === filters.employmentType;
      const matchesStatus = !filters.status || job.status === filters.status;
      
      return matchesTab && matchesSearch && matchesDepartment && matchesLocation && 
             matchesEmploymentType && matchesStatus;
    });

    // Sort jobs
    filteredJobs.sort((a, b) => {
      switch (sortBy) {
        case 'created-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'created-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        case 'applicants-desc':
          return (b.applicants || 0) - (a.applicants || 0);
        case 'applicants-asc':
          return (a.applicants || 0) - (b.applicants || 0);
        case 'salary-desc':
          return parseInt(b.salaryMax || 0) - parseInt(a.salaryMax || 0);
        case 'salary-asc':
          return parseInt(a.salaryMax || 0) - parseInt(b.salaryMax || 0);
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return filteredJobs;
  };

  const filteredJobs = getFilteredAndSortedJobs();

  // Get job counts for tabs
  const getJobCounts = () => {
    const counts = {
      all: jobs.length,
      active: jobs.filter(job => job.status === 'active').length,
      draft: jobs.filter(job => job.status === 'draft').length,
      paused: jobs.filter(job => job.status === 'paused').length,
      closed: jobs.filter(job => job.status === 'closed').length,
    };
    return counts;
  };

  const jobCounts = getJobCounts();

  // Job actions
  const editJob = (job) => {
    navigation.navigate('CreateJobScreen', { 
      job: job, 
      mode: 'edit' 
    });
  };

  const duplicateJob = (job) => {
    navigation.navigate('CreateJobScreen', { 
      job: job, 
      mode: 'duplicate' 
    });
  };

  const deleteJob = async (jobId) => {
    Alert.alert(
      'Delete Job',
      'Are you sure you want to delete this job? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Real DELETE — applications referencing this job will keep their
              // job_id but the JOIN will return null for job fields. Confirm
              // your DB has the right ON DELETE behaviour if that matters.
              const res = await apiFetch(`/api/data/jobs/${jobId}`, { method: 'DELETE' });
              if (res?.error) throw new Error(res.message || 'Delete failed');
              const updatedJobs = jobs.filter(job => String(job.id) !== String(jobId));
              setJobs(updatedJobs);
              await AsyncStorage.setItem('jobs_cache', JSON.stringify(updatedJobs)).catch(() => {});
              Alert.alert('Deleted', 'Job removed successfully');
            } catch (error) {
              console.warn('deleteJob:', error?.message);
              Alert.alert('Could not delete', error?.message || 'Please try again.');
            }
          }
        }
      ]
    );
  };

  const changeJobStatus = async (jobId, newStatus) => {
    try {
      // Real PUT — also toggle is_active so endpoints filtering on it stay
      // consistent with the human-readable status field.
      const payload = {
        status: newStatus,
        is_active: newStatus === 'active',
      };
      const res = await apiFetch(`/api/data/jobs/${jobId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (res?.error) throw new Error(res.message || 'Update failed');

      const updatedJobs = jobs.map(job =>
        String(job.id) === String(jobId)
          ? { ...job, status: newStatus, updatedAt: new Date().toISOString() }
          : job
      );
      setJobs(updatedJobs);
      await AsyncStorage.setItem('jobs_cache', JSON.stringify(updatedJobs)).catch(() => {});

      const statusLabels = { active: 'activated', paused: 'paused', closed: 'closed', draft: 'moved to draft' };
      Alert.alert('Success', `Job ${statusLabels[newStatus] || 'updated'} successfully`);
    } catch (error) {
      console.warn('changeJobStatus:', error?.message);
      Alert.alert('Could not update', error?.message || 'Please try again.');
    }
  };

  const viewJobDetails = (job) => {
    navigation.navigate('PostJobDetailsScreen', { job });
  };

  const viewApplications = (job) => {
    navigation.navigate('ApplicantsPipelineScreen', {
      jobId: job.id,
      jobTitle: job.title,
      applicantCount: job.applicants,
      jobData: {
        title: job.title,
        department: job.department,
        type: job.jobType || job.type || 'Full-time',
        location: job.location,
        salaryMin: job.salaryMin,
        salaryMax: job.salaryMax,
      }
    });
  };

  // Bulk actions
  const toggleJobSelection = (jobId) => {
    if (selectedJobs.includes(jobId)) {
      setSelectedJobs(selectedJobs.filter(id => id !== jobId));
    } else {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  const selectAllJobs = () => {
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(job => job.id));
    }
  };

  const bulkDeleteJobs = async () => {
    if (selectedJobs.length === 0) return;

    Alert.alert(
      'Delete Jobs',
      `Are you sure you want to delete ${selectedJobs.length} job(s)? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // Fire all DELETEs in parallel. If any fails we report it and
              // refresh from server so the UI matches the actual DB state.
              const results = await Promise.allSettled(
                selectedJobs.map(id => apiFetch(`/api/data/jobs/${id}`, { method: 'DELETE' }))
              );
              const failed = results.filter(r => r.status === 'rejected' || r.value?.error).length;
              if (failed > 0) {
                Alert.alert('Partial failure', `${selectedJobs.length - failed} deleted, ${failed} failed. Refreshing list.`);
              } else {
                Alert.alert('Deleted', `${selectedJobs.length} job(s) removed successfully`);
              }
              setSelectedJobs([]);
              setBulkActionMode(false);
              await loadJobs();
            } catch (error) {
              console.warn('bulkDeleteJobs:', error?.message);
              Alert.alert('Could not delete', error?.message || 'Please try again.');
            }
          }
        }
      ]
    );
  };

  const bulkChangeStatus = async (newStatus) => {
    if (selectedJobs.length === 0) return;

    try {
      const payload = { status: newStatus, is_active: newStatus === 'active' };
      const results = await Promise.allSettled(
        selectedJobs.map(id => apiFetch(`/api/data/jobs/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        }))
      );
      const failed = results.filter(r => r.status === 'rejected' || r.value?.error).length;
      const ok = selectedJobs.length - failed;
      setSelectedJobs([]);
      setBulkActionMode(false);

      const statusLabels = { active: 'activated', paused: 'paused', closed: 'closed', draft: 'moved to draft' };
      if (failed > 0) {
        Alert.alert('Partial failure', `${ok} ${statusLabels[newStatus] || 'updated'}, ${failed} failed. Refreshing list.`);
      } else {
        Alert.alert('Success', `${ok} job(s) ${statusLabels[newStatus] || 'updated'} successfully`);
      }
      await loadJobs();
    } catch (error) {
      console.warn('bulkChangeStatus:', error?.message);
      Alert.alert('Could not update', error?.message || 'Please try again.');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      department: '',
      location: '',
      employmentType: '',
      status: '',
    });
    setSearchQuery('');
    setSortBy('created-desc');
  };

  // Header component
  const renderHeader = () => (
    <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.header}>
      <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
      
      <View style={styles.headerContent}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <View style={styles.headerInfo}>
          <View style={styles.headerTitleContainer}>
            {/* Company logo synced from CompanyProfileScreen */}
            <TouchableOpacity
              onPress={() => navigation.navigate('CompanyProfileScreen')}
              style={{ marginRight: 8 }}
            >
              {companyLogo
                ? <Image
                    source={{ uri: companyLogo || undefined }}
                    style={{ width: 34, height: 34, borderRadius: 17,
                      borderWidth: 2, borderColor: 'rgba(255,255,255,0.5)' }}
                    resizeMode="cover"
                  />
                : <View style={{ width: 34, height: 34, borderRadius: 17,
                    backgroundColor: 'rgba(255,255,255,0.2)', borderWidth: 2,
                    borderColor: 'rgba(255,255,255,0.4)',
                    alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>
                      {(companyName || 'CO').split(' ').slice(0, 2)
                        .map(w => w[0]?.toUpperCase() || '').join('') || 'CO'}
                    </Text>
                  </View>}
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Job Management</Text>
          </View>
          <Text style={styles.headerSubtitle}>
            {companyName ? `${companyName} • ` : ''}{jobCounts.all} Total Jobs • {jobCounts.active} Active
          </Text>
        </View>
        
        <TouchableOpacity
          style={styles.headerActionButton}
          onPress={() => navigation.navigate('CreateJobScreen')}
        >
          <MaterialCommunityIcons name="plus" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // Search bar
  const renderSearchBar = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search jobs..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
      
      <TouchableOpacity
        style={styles.filterButton}
        onPress={() => setShowFilters(true)}
      >
        <MaterialCommunityIcons name="filter-variant" size={20} color="#3B82F6" />
      </TouchableOpacity>
    </View>
  );

  // Tabs
  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabsContent}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tab,
              selectedTab === tab.id && styles.activeTab
            ]}
            onPress={() => setSelectedTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={16} 
              color={selectedTab === tab.id ? '#3B82F6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              selectedTab === tab.id && styles.activeTabText
            ]}>
              {tab.label}
            </Text>
            {jobCounts[tab.id] > 0 && (
              <View style={[
                styles.tabBadge,
                selectedTab === tab.id && styles.activeTabBadge
              ]}>
                <Text style={[
                  styles.tabBadgeText,
                  selectedTab === tab.id && styles.activeTabBadgeText
                ]}>
                  {jobCounts[tab.id]}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Controls (sort, bulk actions)
  const renderControls = () => (
    <View style={styles.controlsContainer}>
      <View style={styles.controlsLeft}>
        <TouchableOpacity
          style={styles.sortButton}
          onPress={() => {
            // Simple sort toggle for demo
            setSortBy(sortBy === 'created-desc' ? 'created-asc' : 'created-desc');
          }}
        >
          <MaterialCommunityIcons name="sort" size={16} color="#6B7280" />
          <Text style={styles.sortButtonText}>Sort</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.bulkButton, bulkActionMode && styles.bulkButtonActive]}
          onPress={() => {
            setBulkActionMode(!bulkActionMode);
            setSelectedJobs([]);
          }}
        >
          <MaterialCommunityIcons 
            name="checkbox-multiple-marked" 
            size={16} 
            color={bulkActionMode ? '#FFFFFF' : '#6B7280'} 
          />
          <Text style={[styles.bulkButtonText, bulkActionMode && styles.bulkButtonTextActive]}>
            Bulk
          </Text>
        </TouchableOpacity>
      </View>
      
      <View style={styles.controlsRight}>
        <Text style={styles.resultsText}>
          {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}
        </Text>
      </View>
    </View>
  );

  // Bulk action bar
  const renderBulkActionBar = () => {
    if (!bulkActionMode || selectedJobs.length === 0) return null;

    return (
      <View style={styles.bulkActionBar}>
        <View style={styles.bulkActionLeft}>
          <TouchableOpacity style={styles.selectAllButton} onPress={selectAllJobs}>
            <MaterialCommunityIcons 
              name={selectedJobs.length === filteredJobs.length ? "checkbox-marked" : "checkbox-blank-outline"} 
              size={20} 
              color="#3B82F6" 
            />
            <Text style={styles.selectAllText}>
              {selectedJobs.length === filteredJobs.length ? 'Deselect All' : 'Select All'}
            </Text>
          </TouchableOpacity>
          <Text style={styles.selectedCountText}>
            {selectedJobs.length} selected
          </Text>
        </View>
        
        <View style={styles.bulkActionButtons}>
          <TouchableOpacity
            style={styles.bulkActionButton}
            onPress={() => bulkChangeStatus('active')}
          >
            <MaterialCommunityIcons name="play" size={16} color="#10B981" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.bulkActionButton}
            onPress={() => bulkChangeStatus('paused')}
          >
            <MaterialCommunityIcons name="pause" size={16} color="#F59E0B" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={styles.bulkActionButton}
            onPress={() => bulkChangeStatus('closed')}
          >
            <MaterialCommunityIcons name="stop" size={16} color="#6B7280" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.bulkActionButton, styles.deleteButton]}
            onPress={bulkDeleteJobs}
          >
            <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  // Job card
  const renderJobCard = ({ item: job }) => {
    const isSelected = selectedJobs.includes(job.id);
    
    return (
      <TouchableOpacity
        style={[
          styles.jobCard,
          isSelected && styles.selectedJobCard
        ]}
        onPress={() => {
          if (bulkActionMode) {
            toggleJobSelection(job.id);
          } else {
            viewJobDetails(job);
          }
        }}
        onLongPress={() => {
          if (!bulkActionMode) {
            setBulkActionMode(true);
            setSelectedJobs([job.id]);
          }
        }}
      >
        {bulkActionMode && (
          <View style={styles.selectionCheckbox}>
            <MaterialCommunityIcons 
              name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"} 
              size={20} 
              color={isSelected ? "#3B82F6" : "#9CA3AF"} 
            />
          </View>
        )}
        
        <View style={styles.jobCardContent}>
          <View style={styles.jobCardHeader}>
            <View style={styles.jobCardTitleContainer}>
              <Text style={styles.jobTitle} numberOfLines={1}>
                {job.title}
              </Text>
              <View style={styles.jobBadges}>
                {job.isUrgent && (
                  <View style={styles.urgentBadge}>
                    <Text style={styles.urgentBadgeText}>Urgent</Text>
                  </View>
                )}
                {job.isRemote && (
                  <View style={styles.remoteBadge}>
                    <Text style={styles.remoteBadgeText}>Remote</Text>
                  </View>
                )}
              </View>
            </View>
            
            <View style={[styles.statusBadge, styles[`status${job.status.charAt(0).toUpperCase() + job.status.slice(1)}`]]}>
              <Text style={[styles.statusText, styles[`status${job.status.charAt(0).toUpperCase() + job.status.slice(1)}Text`]]}>
                {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
              </Text>
            </View>
          </View>
          
          <View style={styles.jobCardDetails}>
            <View style={styles.jobDetailRow}>
              <MaterialCommunityIcons name="domain" size={14} color="#6B7280" />
              <Text style={styles.jobDetailText}>{job.department}</Text>
            </View>
            <View style={styles.jobDetailRow}>
              <MaterialCommunityIcons name="map-marker" size={14} color="#6B7280" />
              <Text style={styles.jobDetailText}>{job.location}</Text>
            </View>
            <View style={styles.jobDetailRow}>
              <MaterialCommunityIcons name="currency-gbp" size={14} color="#6B7280" />
              <Text style={styles.jobDetailText}>
                £{parseInt(job.salaryMin || 0).toLocaleString()} - £{parseInt(job.salaryMax || 0).toLocaleString()}
              </Text>
            </View>
          </View>
          
          <View style={styles.jobCardStats}>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="account-group" size={16} color="#3B82F6" />
              <Text style={styles.statText}>{job.applicants || 0} applicants</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="eye" size={16} color="#6B7280" />
              <Text style={styles.statText}>{job.views || 0} views</Text>
            </View>
            <View style={styles.statItem}>
              <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
              <Text style={styles.statText}>
                {new Date(job.createdAt).toLocaleDateString()}
              </Text>
            </View>
          </View>
          
          {!bulkActionMode && (
            <View style={styles.jobCardActions}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => editJob(job)}
              >
                <MaterialCommunityIcons name="pencil" size={16} color="#3B82F6" />
                <Text style={styles.actionButtonText}>Edit</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => viewApplications(job)}
              >
                <MaterialCommunityIcons name="account-group" size={16} color="#10B981" />
                <Text style={styles.actionButtonText}>Applications</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.actionButton}
                onPress={() => duplicateJob(job)}
              >
                <MaterialCommunityIcons name="content-copy" size={16} color="#F59E0B" />
                <Text style={styles.actionButtonText}>Duplicate</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.actionButton, styles.deleteActionButton]}
                onPress={() => deleteJob(job.id)}
              >
                <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
                <Text style={[styles.actionButtonText, styles.deleteActionButtonText]}>Delete</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // Filters modal
  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowFilters(false)}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <TouchableOpacity onPress={() => setShowFilters(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
          <Text style={styles.modalTitle}>Filters & Sort</Text>
          <TouchableOpacity onPress={clearFilters}>
            <Text style={styles.clearFiltersText}>Clear</Text>
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Sort By</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={sortBy}
                onValueChange={setSortBy}
                style={styles.picker}
              >
                {sortOptions.map((option) => (
                  <Picker.Item key={option.value} label={option.label} value={option.value} />
                ))}
              </Picker>
            </View>
          </View>
          
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Department</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={filters.department}
                onValueChange={(value) => setFilters(prev => ({ ...prev, department: value }))}
                style={styles.picker}
              >
                <Picker.Item label="All Departments" value="" />
                {departments.map((dept) => (
                  <Picker.Item key={dept} label={dept} value={dept} />
                ))}
              </Picker>
            </View>
          </View>
          
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Location</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={filters.location}
                onValueChange={(value) => setFilters(prev => ({ ...prev, location: value }))}
                style={styles.picker}
              >
                <Picker.Item label="All Locations" value="" />
                {locations.map((location) => (
                  <Picker.Item key={location} label={location} value={location} />
                ))}
              </Picker>
            </View>
          </View>
          
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Employment Type</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={filters.employmentType}
                onValueChange={(value) => setFilters(prev => ({ ...prev, employmentType: value }))}
                style={styles.picker}
              >
                <Picker.Item label="All Types" value="" />
                {employmentTypes.map((type) => (
                  <Picker.Item key={type.value} label={type.label} value={type.value} />
                ))}
              </Picker>
            </View>
          </View>
        </ScrollView>
        
        <View style={styles.modalFooter}>
          <TouchableOpacity
            style={styles.applyFiltersButton}
            onPress={() => setShowFilters(false)}
          >
            <Text style={styles.applyFiltersButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <StatusBar barStyle="light-content" backgroundColor="#3B82F6" />
        <LinearGradient colors={['#3B82F6', '#1D4ED8']} style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#FFFFFF" />
          <Text style={styles.loadingText}>Loading jobs...</Text>
        </LinearGradient>
      </SafeAreaView>
    );
  }

  return (
    <LinearGradient colors={['#3B82F6', '#3B82F6']} style={styles.fullScreenContainer}>
      <SafeAreaView style={styles.safeContainer}>
        {renderHeader()}
        {renderSearchBar()}
        {renderTabs()}
        {renderControls()}
        {renderBulkActionBar()}

        <View style={styles.contentContainer}>
          <FlatList
            data={filteredJobs}
            renderItem={renderJobCard}
            keyExtractor={(item) => item.id}
            style={styles.jobsList}
            contentContainerStyle={styles.jobsListContent}
            showsVerticalScrollIndicator={false}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
            }
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <MaterialCommunityIcons name="briefcase-outline" size={64} color="#D1D5DB" />
                <Text style={styles.emptyTitle}>No jobs found</Text>
                <Text style={styles.emptySubtitle}>
                  {searchQuery || Object.values(filters).some(f => f) 
                    ? 'Try adjusting your search or filters'
                    : 'Create your first job posting to get started'
                  }
                </Text>
                {!searchQuery && !Object.values(filters).some(f => f) && (
                  <TouchableOpacity
                    style={styles.createJobButton}
                    onPress={() => navigation.navigate('CreateJobScreen')}
                  >
                    <MaterialCommunityIcons name="plus" size={20} color="#FFFFFF" />
                    <Text style={styles.createJobButtonText}>Create Job</Text>
                  </TouchableOpacity>
                )}
              </View>
            }
          />
        </View>

        {renderFiltersModal()}
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  fullScreenContainer: {
    flex: 1,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    flex: 1,
    backgroundColor: '#F9FAFB',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: 10,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#FFFFFF',
    fontSize: 16,
    marginTop: 12,
  },
  
  // Header styles
  header: {
    paddingTop: 20,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
  },
  headerActionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Search bar
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Tabs
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
    gap: 6,
  },
  activeTab: {
    backgroundColor: '#EEF2FF',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  tabBadge: {
    backgroundColor: '#E5E7EB',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center',
  },
  activeTabBadge: {
    backgroundColor: '#3B82F6',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  
  // Controls
  controlsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  controlsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    gap: 4,
  },
  sortButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  bulkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    gap: 4,
  },
  bulkButtonActive: {
    backgroundColor: '#3B82F6',
  },
  bulkButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  bulkButtonTextActive: {
    color: '#FFFFFF',
  },
  controlsRight: {
    alignItems: 'flex-end',
  },
  resultsText: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  // Bulk action bar
  bulkActionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#EEF2FF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  bulkActionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  selectAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectAllText: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '500',
  },
  selectedCountText: {
    fontSize: 14,
    color: '#6B7280',
  },
  bulkActionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  bulkActionButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  deleteButton: {
    borderColor: '#FEE2E2',
    backgroundColor: '#FEF2F2',
  },
  
  // Job list
  jobsList: {
    flex: 1,
  },
  jobsListContent: {
    padding: 20,
    paddingBottom: 100,
  },
  
  // Job card
  jobCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  selectedJobCard: {
    borderColor: '#3B82F6',
    backgroundColor: '#F8FAFF',
  },
  selectionCheckbox: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
  },
  jobCardContent: {
    gap: 12,
  },
  jobCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  jobCardTitleContainer: {
    flex: 1,
    gap: 8,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  jobBadges: {
    flexDirection: 'row',
    gap: 6,
  },
  urgentBadge: {
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  urgentBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#DC2626',
  },
  remoteBadge: {
    backgroundColor: '#DBEAFE',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  remoteBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2563EB',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusActive: {
    backgroundColor: '#D1FAE5',
  },
  statusDraft: {
    backgroundColor: '#FEF3C7',
  },
  statusPaused: {
    backgroundColor: '#FED7AA',
  },
  statusClosed: {
    backgroundColor: '#F3F4F6',
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statusActiveText: {
    color: '#065F46',
  },
  statusDraftText: {
    color: '#92400E',
  },
  statusPausedText: {
    color: '#C2410C',
  },
  statusClosedText: {
    color: '#6B7280',
  },
  jobCardDetails: {
    gap: 6,
  },
  jobDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  jobDetailText: {
    fontSize: 14,
    color: '#6B7280',
  },
  jobCardStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statText: {
    fontSize: 12,
    color: '#6B7280',
  },
  jobCardActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#6B7280',
  },
  deleteActionButton: {
    marginLeft: 'auto',
  },
  deleteActionButtonText: {
    color: '#EF4444',
  },
  
  // Empty state
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  createJobButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3B82F6',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  createJobButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  clearFiltersText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3B82F6',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  filterSection: {
    marginBottom: 24,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
  },
  picker: {
    height: 50,
  },
  modalFooter: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  applyFiltersButton: {
    backgroundColor: '#3B82F6',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
  },
  applyFiltersButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});